// src/audio/AudioEngine.js
//
// Central place for all audio playback. Handles:
//  - loading + looping ambient sound layers (rain, birds, water, melody...)
//  - loading + playing the user's own imported tracks
//  - smooth volume fades (fade-in when a sound starts, fade-out when it stops
//    or when the sleep timer ends)
//  - a master "sleep timer" that gently fades EVERYTHING to silence over the
//    last N minutes instead of cutting off abruptly
//
// This is intentionally framework-light (no Redux etc.) so it's easy to drop
// into any navigation/state setup. Screens call these functions and read
// `AudioEngine.activeLayers` to know what's currently playing.

import { Audio } from "expo-av";
import { Asset } from "expo-asset";

import {
  AppState,
  NativeEventEmitter,
  NativeModules,
  Platform,
} from "react-native";
import * as FileSystem from "expo-file-system";
import BluetoothHeadset from "./BluetoothHeadsetModule";

class AudioEngineClass {
  constructor() {
    this.layers = {}; // id -> { sound, targetVolume, uri, name }
    this.sleepTimerHandle = null;
    this.listeners = new Set();
    this._interrupted = false; // true while audio focus is lost
    this._callInterruption = false; // true while a phone/VoIP call holds audio focus
    this._fadingOutForSleep = false; // true while the sleep-timer fade is running
    this._paused = false; // true when the user explicitly paused via pauseAll()
    this._appStateSubscription = null;
    this._appStatePrev = AppState.currentState;
    this._routeChangeSubscription = null;
    this._iosInterruptionSubscription = null; // iOS AVAudioSession interruption events
    this._routeResumeTimer = null; // debounce timer for BT route-change resumes (4 s safety net)
    this._btQuickResumeTimer = null; // Android-only fast BT resume attempt (~800 ms)
    this._backgroundStopListeners = new Set(); // callbacks for OEM battery-kill events
    this._batteryPromptTimer = null; // delayed fire — cancelled if audio self-resumes
    this._errorListeners = new Set(); // callbacks for layer-load failures
    this._btHeadsetSubscription = null; // Android BT broadcast subscription
    this._phoneCallBeganSubscription = null; // Android phone-call-began subscription
    this._phoneCallEndedSubscription = null; // Android phone-call-ended subscription
  }

  async init() {
    // Cold-restart safety: if the user force-quit the app while a sleep-timer
    // fade was running, all in-memory state (_fadingOutForSleep, _interrupted,
    // layers, fade intervals, timers) was wiped by the OS when the process was
    // killed. The constructor already resets every flag to its safe default, so
    // there is nothing extra to do here. Native Audio objects (AVAudioPlayer on
    // iOS, AudioTrack/MediaPlayer on Android) are also released by the OS when
    // the process dies — no ghost audio can survive a force-quit. The
    // setAudioModeAsync call below establishes a clean session for the new
    // process; it does not inherit any state from the previous process's session.
    await Audio.setAudioModeAsync({
      staysActiveInBackground: true, // keep audio alive when screen locks
      playsInSilentModeIOS: true, // play through the iOS silent/ringer switch
      interruptionModeIOS: 2, // 2 = DuckOthers: lower other audio, don't stop ours
      shouldDuckAndroid: true, // lower competing audio on Android instead of pausing
      interruptionModeAndroid: 1, // 1 = DoNotMix: request exclusive audio focus on Android
    });

    // Resume playback when the app returns to the foreground after an
    // interruption (incoming call, alarm, another app taking audio focus).
    this._appStateSubscription = AppState.addEventListener(
      "change",
      (nextState) => {
        const prev = this._appStatePrev;
        this._appStatePrev = nextState;

        const returningToForeground =
          nextState === "active" &&
          (prev === "background" || prev === "inactive");

        if (returningToForeground) {
          // Check file existence for every active layer when returning to the
          // foreground. A file can disappear while the sound is still buffered
          // (storage eviction, reinstall) — this is the earliest opportunity to
          // detect the gap and surface the warning to the user.
          this._checkActiveLayersExist();
        }

        if (
          returningToForeground &&
          this._interrupted &&
          !this._callInterruption
        ) {
          // Do not resume if the sleep timer is actively fading everything out.
          // If the app was backgrounded during a sleep-timer fade and a BT
          // device reconnects while it's in the background, the OS will fire
          // this AppState listener when the user brings the app back to the
          // foreground. Without this guard the _interrupted flag would be
          // cleared and _resumeAllLayers() called, restarting audio the user
          // expected the sleep timer to silence. Returning early keeps the
          // _interrupted flag set so the sleep-timer path continues to own the
          // fade; the BT-reconnect timers are intentionally left in place so
          // that if the fade completes and audio is later restarted manually,
          // any residual timer fires still exit via the _fadingOutForSleep
          // guard in _scheduleRouteChangeResume.
          //
          // _callInterruption is set when a phone/VoIP call holds audio focus.
          // On most iOS devices the call moves the app to 'inactive', so this
          // path is never reached during the call anyway. On OEM Android builds
          // (and some VoIP scenarios) the app can stay 'active' throughout a
          // call — without the _callInterruption guard, returning to foreground
          // while the call is still ongoing would attempt to resume audio. The
          // guard keeps _interrupted set until _handleCallInterruptionEnded()
          // clears _callInterruption and calls _resumeAllLayers() directly.
          if (this._fadingOutForSleep) return;
          this._interrupted = false;
          // Cancel any pending BT-reconnect timers. If a phone call arrived
          // while AirPods were mid-reconnect (both paths running concurrently),
          // both the 4-second route-change timer and the 800 ms Android fast-
          // resume timer may still be scheduled. Clearing them here ensures that
          // whichever path wins — AppState (call ended, app foregrounded) or the
          // timer — the other one cannot fire and attempt a second resume.
          // Without this, _markInterrupted() being called again in the brief
          // window between this flag clear and the timer's own check could let
          // the timer see _interrupted = true and trigger a duplicate resume.
          if (this._routeResumeTimer) {
            clearTimeout(this._routeResumeTimer);
            this._routeResumeTimer = null;
          }
          if (this._btQuickResumeTimer) {
            clearTimeout(this._btQuickResumeTimer);
            this._btQuickResumeTimer = null;
          }
          this._resumeAllLayers();
        }
      },
    );

    // Listen for audio route changes (e.g. Bluetooth headset reconnecting) and
    // AVAudioSession interruption events (e.g. incoming phone/VoIP call).
    //
    // On iOS, AVAudioSession posts both route-change and interruption notifications
    // via the NativeEventEmitter on the RCTAudioSession module when available (e.g.
    // react-native-audio-session). We try to attach; if the module isn't present
    // we fall back to the delayed-resume path already wired into
    // _markInterrupted() via _scheduleRouteChangeResume().
    //
    // Interruption events set/clear _callInterruption so that resume timers and
    // the AppState path never play audio back into an ongoing phone call — even on
    // VoIP scenarios or OEM builds that keep AppState === 'active' during a call.
    if (Platform.OS === "ios") {
      const audioSessionModule =
        NativeModules.RCTAudioSession ||
        NativeModules.RNAudioSession ||
        NativeModules.AudioSession ||
        null;

      if (audioSessionModule) {
        const emitter = new NativeEventEmitter(audioSessionModule);
        this._routeChangeSubscription = emitter.addListener(
          "audioRouteChanged",
          (event) => this._handleRouteChange(event),
        );

        // AVAudioSession fires a single 'audioSessionInterruption' event with a
        // 'type' field: 'began' when another app (phone call, Siri, alarm) takes
        // audio focus, and 'ended' when focus is returned. Some modules instead
        // emit separate 'audioInterruptionBegan' / 'audioInterruptionEnded' events;
        // we subscribe to both forms so we work with any compatible module.
        this._iosInterruptionSubscription = emitter.addListener(
          "audioSessionInterruption",
          (event) => {
            const type = event?.type ?? event?.interruptionType;
            if (type === "began" || type === 1) {
              this._handleCallInterruptionBegan();
            } else if (type === "ended" || type === 0) {
              this._handleCallInterruptionEnded();
            }
          },
        );

        // Fallback: some modules emit discrete began/ended events instead of a
        // unified event with a type field. Subscribe to both so we work regardless.
        emitter.addListener("audioInterruptionBegan", () =>
          this._handleCallInterruptionBegan(),
        );
        emitter.addListener("audioInterruptionEnded", () =>
          this._handleCallInterruptionEnded(),
        );
      }
    }

    // Android: subscribe to the native BluetoothHeadset BroadcastReceiver event.
    // The native module (BluetoothHeadsetModule.java) emits "btHeadsetConnected"
    // the moment BluetoothHeadset.ACTION_CONNECTION_STATE_CHANGED arrives with
    // STATE_CONNECTED — typically within 50-150 ms of the headset reconnecting.
    //
    // BLUETOOTH_CONNECT is a dangerous runtime permission on Android 12+ (API 31+).
    // We request it here before relying on the broadcast; without the grant,
    // ACTION_CONNECTION_STATE_CHANGED is not delivered on API 31+ and the
    // "instant resume" path is silently inactive. If the user denies the
    // permission (or is on a pre-12 device where it is not needed), the 800 ms
    // and 4-second safety-net timers in _scheduleRouteChangeResume() continue
    // to handle reconnects — no user-visible regression.
    //
    // We also subscribe to phone call state events. The native module registers a
    // TelephonyManager.ACTION_PHONE_STATE_CHANGED receiver (no extra permission
    // needed for EXTRA_STATE) and emits "phoneCallBegan" / "phoneCallEnded".
    // These set/clear _callInterruption on OEM builds that keep AppState ===
    // 'active' during a call — the exact carrier-dependent scenario where the
    // AppState guard alone is insufficient.
    if (Platform.OS === "android") {
      const btPermissionGranted = await BluetoothHeadset.requestPermission();
      if (btPermissionGranted) {
        // Retry receiver registration now that the permission is confirmed.
        // The native module attempted registration at startup; on Android 12+
        // the permission may not have been granted yet at that point.
        BluetoothHeadset.retryRegistration();
      }
      // Add the BT listener regardless of permission result — if permission was
      // denied the broadcast never fires but the listener is still a no-op safe
      // to hold (and the timer fallbacks remain active).
      this._btHeadsetSubscription = BluetoothHeadset.addConnectedListener(
        () => {
          this._handleRouteChange({ reason: 1 });
        },
      );

      // Phone call state: set _callInterruption when a call starts so that the
      // 800 ms and 4-second resume timers don't fire into an ongoing call.
      this._phoneCallBeganSubscription =
        BluetoothHeadset.addPhoneCallBeganListener(() =>
          this._handleCallInterruptionBegan(),
        );
      // When the call ends, clear _callInterruption and resume if we were
      // interrupted (audio was paused by the call and _interrupted is still set).
      this._phoneCallEndedSubscription =
        BluetoothHeadset.addPhoneCallEndedListener(() =>
          this._handleCallInterruptionEnded(),
        );
    }
  }

  // Register a callback to be notified when audio stops unexpectedly while the
  // app is in the background on Android — the signature of an OEM battery-saver
  // kill. Returns an unsubscribe function.
  onUnexpectedBackgroundStop(cb) {
    this._backgroundStopListeners.add(cb);
    return () => this._backgroundStopListeners.delete(cb);
  }

  // Register a callback to be notified when a layer fails to load (e.g. the
  // backing file is missing). The callback receives { id, error }. Returns an
  // unsubscribe function. This lets UI components react to load failures that
  // occur inside AudioEngine without polling or silent fallbacks.
  onLayerError(cb) {
    this._errorListeners.add(cb);
    return () => this._errorListeners.delete(cb);
  }

  // Called when we detect a sound stopped unexpectedly mid-session.
  // Also schedules a delayed auto-resume to handle Bluetooth reconnects:
  // when BT disconnects the OS pauses audio, and when it reconnects
  // (typically within 1-5 s) we want to resume without user interaction.
  _markInterrupted() {
    const wasInterrupted = this._interrupted;
    if (!wasInterrupted) {
      this._interrupted = true;

      // If we're on Android and in the background, defer the battery prompt
      // until we know this was not a transient interruption.
      if (
        Platform.OS === "android" &&
        AppState.currentState !== "active" &&
        this._backgroundStopListeners.size > 0
      ) {
        if (this._batteryPromptTimer) {
          clearTimeout(this._batteryPromptTimer);
        }
        this._batteryPromptTimer = setTimeout(async () => {
          this._batteryPromptTimer = null;
          if (!this._interrupted) return;

          for (const [, layer] of Object.entries(this.layers)) {
            try {
              const status = await layer.sound.getStatusAsync();
              if (status.isLoaded && status.isPlaying) return;
            } catch (_) {}
          }

          this._backgroundStopListeners.forEach((cb) => {
            try {
              cb();
            } catch (_) {}
          });
        }, 8000);
      }
    }

    // A late status callback can arrive after another path set _interrupted.
    // Ensure the resume timers still exist, without resetting an active pair.
    if (!this._routeResumeTimer && !this._btQuickResumeTimer) {
      this._scheduleRouteChangeResume();
    }
  }


  // Called when the OS signals that audio focus has been taken by a phone call
  // (iOS: AVAudioSession interruption-began; Android: phoneCallBegan event from
  // TelephonyManager). Sets _callInterruption = true so that the 800 ms and
  // 4-second resume timers, the AppState listener, and the route-change handler
  // all know NOT to resume audio into the ongoing call — even on OEM builds and
  // VoIP scenarios that keep AppState === 'active' during a call.
  //
  // Also cancels any currently scheduled resume timers that fired before the
  // interruption event arrived (event delivery is asynchronous; the timer may
  // have been scheduled by _markInterrupted() a fraction of a second before
  // the interruption event lands in JS).
  _handleCallInterruptionBegan() {
    this._callInterruption = true;

    if (this._routeResumeTimer) {
      clearTimeout(this._routeResumeTimer);
      this._routeResumeTimer = null;
    }
    if (this._btQuickResumeTimer) {
      clearTimeout(this._btQuickResumeTimer);
      this._btQuickResumeTimer = null;
    }
  }

  // Called when the OS signals that audio focus has been returned after a call
  // (iOS: AVAudioSession interruption-ended; Android: phoneCallEnded event from
  // TelephonyManager). Clears _callInterruption and, if audio was interrupted
  // while the call was in progress, resumes all layers immediately.
  //
  // On iOS, AVAudioSession's interruption-ended event is the authoritative signal
  // that the session is ready to play again — no need to wait for AppState to
  // transition back to 'active'. On Android, phoneCallEnded fires when
  // TelephonyManager reports CALL_STATE_IDLE, which is equally authoritative.
  async _handleCallInterruptionEnded() {
    this._callInterruption = false;

    // Only resume if we were actually interrupted (audio was stopped by the
    // call). If the user paused manually before the call started, honour that.
    if (this._interrupted && !this._fadingOutForSleep && !this._paused) {
      this._interrupted = false;
      // Cancel any pending timers so they don't race with this direct resume.
      if (this._routeResumeTimer) {
        clearTimeout(this._routeResumeTimer);
        this._routeResumeTimer = null;
      }
      if (this._btQuickResumeTimer) {
        clearTimeout(this._btQuickResumeTimer);
        this._btQuickResumeTimer = null;
      }
      await this._resumeAllLayers();
    }
  }

  // Attempt to resume stopped layers after a short delay. This covers the
  // Bluetooth reconnect case where the OS reroutes audio and restarts the
  // output within a few seconds. We debounce to avoid spamming resume calls
  // when multiple layers stop in quick succession.
  //
  // On Android there is no route-change event equivalent to iOS AVAudioSession,
  // so we rely purely on timers. To bring audio back in under 1 second we fire
  // a fast attempt at 800 ms (Android only): BT headsets typically complete
  // audio routing within that window. If the attempt succeeds the 4-second
  // safety-net timer exits early via the _interrupted guard; if BT isn't ready
  // yet the fast attempt's playAsync calls will cause the status callback to
  // re-fire _markInterrupted(), which reschedules both timers and the device
  // gets another chance at 800 ms + 4 s from that point.
  _scheduleRouteChangeResume() {
    if (this._routeResumeTimer) {
      clearTimeout(this._routeResumeTimer);
    }
    if (this._btQuickResumeTimer) {
      clearTimeout(this._btQuickResumeTimer);
      this._btQuickResumeTimer = null;
    }

    // Android fast path: attempt resume at 800 ms so audio comes back in
    // under 1 s on most devices. The 4-second timer below remains as a
    // safety net in case routing takes longer than expected.
    //
    // Critically: we do NOT clear _interrupted until we confirm that at least
    // one layer is actually playing after the attempt. If BT routing isn't
    // ready yet and nothing successfully resumes, _interrupted stays true so
    // the 4-second fallback fires normally. If playAsync() calls fail or the
    // OS immediately stops the sounds again, the status callbacks will re-fire
    // _markInterrupted() and reschedule a fresh pair of timers.
    if (Platform.OS === "android") {
      this._btQuickResumeTimer = setTimeout(async () => {
        this._btQuickResumeTimer = null;
        if (AppState.currentState !== "active") return;
        if (!this._interrupted) return;
        // A call interruption is active — audio focus has not been returned yet.
        // Don't resume; _handleCallInterruptionEnded() will resume when the call ends.
        if (this._callInterruption) return;
        if (this._fadingOutForSleep) return;
        if (this._paused) return;

        // Attempt to restart every stopped layer. We do not touch _interrupted
        // yet — that only changes if we can confirm at least one layer resumed.
        for (const [, layer] of Object.entries(this.layers)) {
          try {
            const status = await layer.sound.getStatusAsync();
            if (status.isLoaded && !status.isPlaying) {
              await layer.sound.setVolumeAsync(layer.targetVolume);
              await layer.sound.playAsync();
            }
          } catch (_) {}
        }

        // Confirm that at least one layer is now actually playing. This
        // distinguishes a successful BT resume from a silent failure where
        // the OS accepted playAsync() but immediately stopped the sound again
        // because routing wasn't ready.
        let anyPlaying = false;
        for (const [, layer] of Object.entries(this.layers)) {
          try {
            const status = await layer.sound.getStatusAsync();
            if (status.isLoaded && status.isPlaying) anyPlaying = true;
          } catch (_) {}
          if (anyPlaying) break;
        }

        if (!anyPlaying) {
          // BT routing isn't ready yet. Leave _interrupted = true so the
          // 4-second fallback fires. The failed playAsync() calls above will
          // also cause status callbacks to re-fire _markInterrupted(), which
          // reschedules a fresh pair of timers for another attempt.
          return;
        }

        // At least one layer is playing — BT routing succeeded. Cancel the
        // 4-second fallback (it would be a no-op anyway, but this is explicit),
        // clear the interrupted flag, cancel any pending battery prompt, and
        // notify subscribers.
        if (this._routeResumeTimer) {
          clearTimeout(this._routeResumeTimer);
          this._routeResumeTimer = null;
        }
        if (this._batteryPromptTimer) {
          clearTimeout(this._batteryPromptTimer);
          this._batteryPromptTimer = null;
        }
        this._interrupted = false;
        this._notify();
      }, 800);
    }

    // 4-second safety net — primary path on iOS (without route-change event
    // module), and a guaranteed backstop on Android.
    this._routeResumeTimer = setTimeout(async () => {
      this._routeResumeTimer = null;
      // Only resume if the app is in the foreground; if it's in the background
      // the AppState listener will handle it when the app becomes active again.
      if (AppState.currentState !== "active") return;
      // Only resume if we haven't already cleared the interrupted flag via the
      // AppState path or the Android fast-resume path above.
      if (!this._interrupted) return;
      // A call interruption is active — audio focus has not been returned yet.
      // Don't resume; _handleCallInterruptionEnded() will resume when the call ends.
      if (this._callInterruption) return;
      // Do not resume if the sleep timer is actively fading everything out.
      // Clearing the interrupted flag here would let a BT reconnect restart
      // audio the user expected the sleep timer to silence.
      if (this._fadingOutForSleep) return;
      this._interrupted = false;
      await this._resumeAllLayers();
    }, 4000);
  }

  // Called when an audio route change event is received from the OS (iOS only,
  // via AVAudioSession). reason values mirror AVAudioSessionRouteChangeReason:
  //   1 = NewDeviceAvailable (headset/BT connected)
  //   2 = OldDeviceUnavailable (headset/BT disconnected)
  //   others = CategoryChange, Override, WakeFromSleep, etc.
  _handleRouteChange(event) {
    const reason = event?.reason ?? event?.routeChangeReason;

    if (reason === 1 /* NewDeviceAvailable — BT/headset just connected */) {
      // Always cancel both pending timers, even if we cannot resume right now.
      //
      // The critical mid-call scenario:
      //   1. Mix is playing; AirPods disconnect → _markInterrupted() schedules
      //      _routeResumeTimer (4 s) and _btQuickResumeTimer (800 ms Android).
      //   2. A phone call arrives; the app goes inactive/background.
      //   3. AirPods reconnect while the call is still in progress (this event
      //      fires, reason = 1). The app is NOT active, so we must not resume
      //      yet — but we DO cancel both timers here. If we left them alive
      //      they would fire while the call is ongoing and attempt a spurious
      //      resume. When the call ends the AppState listener transitions to
      //      'active', finds _interrupted = true, and calls _resumeAllLayers()
      //      exactly once — no double-resume, no zombie timers.
      if (this._routeResumeTimer) {
        clearTimeout(this._routeResumeTimer);
        this._routeResumeTimer = null;
      }
      if (this._btQuickResumeTimer) {
        clearTimeout(this._btQuickResumeTimer);
        this._btQuickResumeTimer = null;
      }
      // Do not resume if the sleep timer is actively fading everything out.
      // A BT reconnect must not restart audio the user expected the sleep
      // timer to silence.
      if (this._fadingOutForSleep) return;
      // A call interruption is still active — audio focus hasn't been returned.
      // Leave _interrupted set; _handleCallInterruptionEnded() will resume once
      // the call ends and the OS returns focus, whether or not the AirPods are
      // connected at that point.
      if (this._callInterruption) return;
      // Only resume immediately when the app is in the foreground. If a call
      // is still in progress (app inactive/background), leave _interrupted set
      // so the AppState listener handles the single resume when focus returns.
      if (this._interrupted && AppState.currentState === "active") {
        this._interrupted = false;
        this._resumeAllLayers();
      }
    }
    // reason === 2 (OldDeviceUnavailable) means the BT device just disconnected.
    // The playback status callbacks will already call _markInterrupted() when
    // they notice the sounds stopped, so no extra action is needed here.
  }

  // Re-play any layer whose sound has stopped (e.g. after call ends).
  // Skips resume entirely if the sleep timer is already fading everything out —
  // resuming mid-fade would fight the timer and restart audio that should stay
  // silent. Also skips if the user intentionally paused via pauseAll() — a BT
  // reconnect or call-end event must not override an explicit user pause.
  async _resumeAllLayers() {
    // Cancel any pending battery-prompt timer. Reaching this point means audio
    // is self-resuming (call ended, BT reconnected, etc.) — not a battery kill.
    if (this._batteryPromptTimer) {
      clearTimeout(this._batteryPromptTimer);
      this._batteryPromptTimer = null;
    }

    if (this._fadingOutForSleep) {
      // Sleep timer is actively fading out; honour that intent and stay silent.
      return;
    }

    if (this._paused) {
      // User deliberately paused; do not resume on their behalf.
      return;
    }

    // Before resuming, verify that every active layer's backing file still
    // exists. A file can disappear while audio is buffered (storage eviction,
    // external deletion). Calling this here means we catch the gap on
    // interruption-resume paths (call end, BT reconnect) in addition to the
    // foreground-event path wired into the AppState listener.
    // Non-file URIs (bundled assets and test/platform handles) cannot be
    // meaningfully checked through expo-file-system and need no async yield.
    // Local imported tracks remain guarded before they are resumed.
    const hasLocalTrack = Object.values(this.layers).some(
      (layer) => typeof layer.uri === "string" && layer.uri.startsWith("file://"),
    );
    if (hasLocalTrack) {
      await this._checkActiveLayersExist();
    }

    for (const [, layer] of Object.entries(this.layers)) {
      try {
        const status = await layer.sound.getStatusAsync();
        if (status.isLoaded && !status.isPlaying) {
          await layer.sound.setVolumeAsync(layer.targetVolume);
          await layer.sound.playAsync();
        }
      } catch {
        // Sound may be in an unrecoverable state; leave it alone.
      }
    }

    // Notify subscribers (including MediaSessionManager) so the lock-screen
    // card title and play/pause state are re-synced after the interruption.
    // Without this call, RNTP's silent-track session can remain paused on the
    // OS side — causing the lock-screen controls to show stale state or, on
    // some Android OEMs, deregister the play/pause handlers entirely.
    this._notify();
  }

  onChange(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  _notify() {
    this.listeners.forEach((cb) => cb(this.getState()));
  }

  _clearNativeLoopDip(sound, layer) {
    const timer = this._loopBoundaryTimers?.get(sound);
    if (timer) {
      clearTimeout(timer);
      this._loopBoundaryTimers.delete(sound);
    }
    if (layer) layer._nativeLoopSmoothing = false;
  }

  _attachLayerStatus(id, layer, sound) {
    if (typeof sound?.setOnPlaybackStatusUpdate !== "function") return;

    sound.setOnPlaybackStatusUpdate((status) => {
      if (this.layers[id] !== layer || layer.sound !== sound) return;

      // Keep the native loop's single decoder instance, but soften the exact
      // boundary with a short, cancellable volume dip. This is deliberately
      // much smaller than a two-instance crossfade so pause, interruption,
      // sleep-timer, and stop ownership stay unchanged.
      if (
        layer.loop &&
        status.isLoaded &&
        status.didJustFinish &&
        !layer._nativeLoopSmoothing
      ) {
        layer._nativeLoopSmoothing = true;
        this._loopBoundaryTimers = this._loopBoundaryTimers || new Map();
        const dipVolume = Math.max(0, layer.targetVolume * 0.86);
        sound.setVolumeAsync(dipVolume).catch(() => {});
        const timer = setTimeout(async () => {
          this._loopBoundaryTimers.delete(sound);
          if (
            this.layers[id] !== layer ||
            layer.sound !== sound ||
            this._paused ||
            this._fadingOutForSleep
          ) {
            layer._nativeLoopSmoothing = false;
            return;
          }
          try {
            await sound.setVolumeAsync(layer.targetVolume);
          } catch (_) {}
          layer._nativeLoopSmoothing = false;
        }, 80);
        this._loopBoundaryTimers.set(sound, timer);
      }

      if (
        status.isLoaded &&
        status.shouldPlay &&
        !status.isPlaying &&
        !status.isBuffering &&
        !status.didJustFinish &&
        this.layers[id]
      ) {
        this._markInterrupted();
      }
    });
  }

  getState() {
    return Object.entries(this.layers).map(([id, layer]) => ({
      id,
      name: layer.name,
      volume: layer.targetVolume,
      playing: true,
      artworkUri: layer.artworkUri ?? null,
    }));
  }

  // Start (or update) a looping layer at a given volume, fading in smoothly.
  async playLayer(
    id,
    { uri, name, volume = 0.7, loop = true, fadeMs = 2500, artworkUri = null },
  ) {
    // Starting a new layer always clears the paused state — if the user is
    // adding sounds they intend for audio to be playing.
    this._paused = false;

    let layer = this.layers[id];

    if (!layer) {
      // Mark this id as mid-createAsync so stopLayer can distinguish a genuine
      // in-flight race from a routine defensive stop on an already-absent layer.
      this._pendingCreates = this._pendingCreates || new Set();
      this._pendingStops = this._pendingStops || new Set();
      this._pendingCreates.add(id);

      let sound;
      let source;
      try {
        // Bundled assets loaded via require() resolve to a plain number
        // (a Metro asset module ID). In production/release builds, expo-av's
        // createAsync() needs a real resolved URI, not the bare number —
        // Asset.fromModule().downloadAsync() reliably resolves it to a
        // loadable local URI in both dev and release builds. String uris
        // (real file:// paths, like user-imported tracks) are already
        // loadable and skip this resolution step.
        if (typeof uri === "number") {
          const asset = Asset.fromModule(uri);
          await asset.downloadAsync();
          console.warn(
            "[AudioEngine] asset debug:",
            JSON.stringify({
              localUri: asset.localUri,
              uri: asset.uri,
              name: asset.name,
              type: asset.type,
            }),
          );
          source = { uri: asset.localUri || asset.uri };
        } else {
          source = { uri };
        }
        const { sound: newSound } = await Audio.Sound.createAsync(source, {
          isLooping: loop,
          volume: 0,
        });
        sound = newSound;
      } catch (err) {
        // File is missing or unreadable — surface an actionable error instead
        // of failing silently. Clear the in-flight marker, notify any registered
        // error listeners (e.g. the UI layer), log a clear warning, then
        // re-throw so the caller (e.g. SoundTile) can also react.
        this._pendingCreates.delete(id);
        console.warn(
          '[AudioEngine] playLayer: failed to load layer "' +
            id +
            '" from URI ' +
            uri +
            ":",
          err?.message ?? err,
        );
        this._errorListeners.forEach((cb) => {
          try {
            cb({ id, error: err });
          } catch (_) {}
        });
        throw err;
      } finally {
        // Always clear the in-flight marker (handles the success path; the
        // catch block above handles the failure path before re-throwing).
        this._pendingCreates.delete(id);
      }

      // Check whether stopLayer was called for this id while createAsync was
      // in-flight. stopLayer only records the intent when the id is in
      // _pendingCreates, so this check cannot fire from a stale defensive stop.
      if (this._pendingStops.has(id)) {
        this._pendingStops.delete(id);
        try {
          await sound.stopAsync();
          await sound.unloadAsync();
        } catch (_) {}
        return;
      }

      layer = {
        sound,
        targetVolume: volume,
        uri,
        name,
        loop,
        artworkUri,
        source,
      };
      this.layers[id] = layer;
      this._attachLayerStatus(id, layer, sound);
      await sound.playAsync();
      this._fadeTo(sound, 0, volume, fadeMs);
    } else {
      layer.targetVolume = volume;
      this._fadeTo(
        layer.sound,
        await this._currentVolume(layer.sound),
        volume,
        fadeMs,
      );
    }
    this._notify();
  }

  // Stop a layer, fading it out first instead of an abrupt cut.
  // Any in-progress fade (e.g. a fade-in that started via playLayer) is
  // cancelled immediately so the sound doesn't keep rising while we wait for
  // the fade-out to begin.
  //
  // Concurrency safety: if two stopLayer calls race on the same id (e.g. the
  // sleep timer's stopAll is mid-fade and the user manually stops the layer),
  // the second call cancels the first's _fadeTo and also resolves its Promise
  // (via the resolver stored in _fadeIntervals). This prevents the first call
  // from leaking a forever-pending Promise. After _fadeTo resolves, we
  // re-check that the layer still exists before attempting stop/unload, so
  // whichever call wins the race handles cleanup and the other exits cleanly.
  async stopLayer(id, { fadeMs = 2500 } = {}) {
    const layer = this.layers[id];
    if (!layer) {
      // Only record a pending stop if playLayer is currently mid-createAsync
      // for this id. A defensive stop on an already-absent layer (e.g. after
      // a sleep-timer fade-out) must not poison the next playLayer call.
      this._pendingCreates = this._pendingCreates || new Set();
      this._pendingStops = this._pendingStops || new Set();
      if (this._pendingCreates.has(id)) {
        this._pendingStops.add(id);
      }
      return;
    }

    this._clearNativeLoopDip(layer.sound, layer);

    // Cancel any in-progress fade for this sound before querying its current
    // volume. Without this guard a fade-in interval would keep ticking between
    // the stopLayer call and the _fadeTo(…0…) call, causing a brief volume
    // bump that makes the layer appear to "fight" the stop.
    // We also resolve the old fade's Promise so any caller awaiting it (e.g.
    // the sleep timer's stopAll) unblocks immediately instead of leaking.
    if (this._fadeIntervals?.has(layer.sound)) {
      const existing = this._fadeIntervals.get(layer.sound);
      clearInterval(existing.handle);
      existing.resolve(); // unblock any caller awaiting the previous _fadeTo
      this._fadeIntervals.delete(layer.sound);
    }

    // If updateActiveLayersByUri is mid-crossfade it has stored the incoming
    // replacement sound in layer._swapSound. Cancel its fade and unload it now
    // so the new sound doesn't orphan while we fade the old sound to silence.
    //
    // This also covers the sleep-timer + re-import race: stopAll (called by the
    // sleep timer) delegates to stopLayer for each id, so if a re-import
    // crossfade starts before stopLayer reads layer._swapSound, the incoming
    // sound is caught and unloaded here before the timer's fade-out begins.
    if (layer._swapSound) {
      const swapSound = layer._swapSound;
      layer._swapSound = null;
      await this._discardSwapSound(swapSound);
    }

    const from = await this._currentVolume(layer.sound);
    await this._fadeTo(layer.sound, from, 0, fadeMs);

    // A concurrent stopLayer call (e.g. user tapping stop while the sleep
    // timer's fade was in progress) may have already cleaned up this layer
    // while we were awaiting _fadeTo. If so, exit without double-stopping.
    if (!this.layers[id]) return;

    try {
      await layer.sound.stopAsync();
      await layer.sound.unloadAsync();
    } catch (_) {
      // Sound may have already been unloaded (e.g. if updateActiveLayersByUri
      // swapped layer.sound to an already-unloaded object mid-crossfade).
      // Always clean up the layer entry and notify regardless.
    } finally {
      delete this.layers[id];
      this._notify();
    }
  }

  async setLayerVolume(id, volume, fadeMs = 400) {
    const layer = this.layers[id];
    if (!layer) return;
    layer.targetVolume = volume;

    // During a loop handoff, the incoming sound owns the user's target volume
    // while the outgoing sound must keep its independent fade to silence.
    // Otherwise a slider change would cancel the outgoing tail fade.
    // Cancel any in-progress fade *before* reading the current volume so we
    // capture a stable starting point. Without this, a running fade ticker
    // could shift the volume between the getStatusAsync call and the _fadeTo
    // call, producing an audible snap or stutter during fast slider drags.
    this._fadeIntervals = this._fadeIntervals || new Map();
    if (this._fadeIntervals.has(layer.sound)) {
      const existing = this._fadeIntervals.get(layer.sound);
      clearInterval(existing.handle);
      existing.resolve(); // unblock any awaiting caller
      this._fadeIntervals.delete(layer.sound);
    }

    // Stamp this call with a per-layer sequence number so that if a newer
    // setLayerVolume arrives while we are awaiting _currentVolume (async), the
    // older call can detect it is stale and bail out instead of starting a fade
    // that would immediately be cancelled — or worse, cancel the newer fade.
    this._volumeSeq = this._volumeSeq || new Map();
    const seq = (this._volumeSeq.get(id) ?? 0) + 1;
    this._volumeSeq.set(id, seq);

    const from = await this._currentVolume(layer.sound);

    // A newer setLayerVolume call arrived while we were awaiting; let it own
    // the fade instead of racing it.
    if (this._volumeSeq.get(id) !== seq) return;

    this._fadeTo(layer.sound, from, volume, fadeMs);
  }

  async stopAll({ fadeMs = 4000 } = {}) {
    const ids = Object.keys(this.layers);
    await Promise.all(ids.map((id) => this.stopLayer(id, { fadeMs })));
    // All layers are gone — reset the paused flag so the next playLayer()
    // starts fresh rather than inheriting stale paused state.
    this._paused = false;
  }

  // Pause all layers without unloading them — used by lock-screen / media-session
  // play/pause controls so the session can be resumed without reloading every sound.
  async pauseAll() {
    if (this._paused) return;
    // If the sleep timer is already fading everything out, ignore lock-screen
    // pause commands. The sounds are already going to silence; accepting a
    // pause here would set _paused = true with nothing to resume against,
    // leaving the session stuck as paused after the timer completes.
    if (this._fadingOutForSleep) return;
    this._paused = true;

    // Cancel any pending BT-reconnect / interruption resume timer. The
    // status-update callbacks will have fired _markInterrupted() when each
    // sound is paused below, which would schedule a 4-second auto-resume.
    // That timer must not fire and undo an intentional user pause.
    if (this._routeResumeTimer) {
      clearTimeout(this._routeResumeTimer);
      this._routeResumeTimer = null;
    }
    if (this._btQuickResumeTimer) {
      clearTimeout(this._btQuickResumeTimer);
      this._btQuickResumeTimer = null;
    }
    // The interrupted flag is cleared so that the AppState listener also
    // won't attempt an auto-resume if the app moves to background and back.
    this._interrupted = false;
    for (const [, layer] of Object.entries(this.layers)) {
      try {
        const status = await layer.sound.getStatusAsync();
        if (status.isLoaded && status.isPlaying) {
          await layer.sound.pauseAsync();
        }
      } catch (_) {}
    }
    this._notify();
  }

  // Resume all paused layers — counterpart to pauseAll().
  // Skips resume when the sleep timer is actively fading out.
  async resumeAll() {
    if (!this._paused) return;
    this._paused = false;
    if (this._fadingOutForSleep) return;
    for (const [, layer] of Object.entries(this.layers)) {
      try {
        const status = await layer.sound.getStatusAsync();
        if (status.isLoaded && !status.isPlaying) {
          await layer.sound.playAsync();
        }
      } catch (_) {}
    }
    this._notify();
  }

  // Returns true if there is at least one active layer and audio is not paused.
  isPlaying() {
    return Object.keys(this.layers).length > 0 && !this._paused;
  }

  // Check whether the backing file for every active layer still exists on disk.
  // Layers backed by remote http(s) URLs or bundled asset URIs are skipped —
  // only local file:// paths can go missing in the way this guard targets.
  //
  // For any layer whose file is gone:
  //  1. The sound is stopped and unloaded so it cannot continue playing silently.
  //  2. The layer entry is removed from this.layers.
  //  3. Every registered error listener is fired with { id, error } so the UI
  //     can switch the tile to warning/needsReimport state immediately.
  //
  // This is called on every foreground transition (AppState → active) and at
  // the beginning of _resumeAllLayers() so that a missing file is surfaced at
  // the earliest possible moment regardless of how the gap was triggered.
  async _checkActiveLayersExist() {
    const entries = Object.entries(this.layers);
    if (entries.length === 0) return;

    await Promise.all(
      entries.map(async ([id, layer]) => {
        const uri = layer.uri;
        if (typeof uri !== "string" || !uri.startsWith("file://")) return;

        let exists = true;
        try {
          const info = await FileSystem.getInfoAsync(uri);
          exists = info.exists;
        } catch (err) {
          // If the check itself fails (e.g. permission error), leave the layer
          // alone — better to let it keep playing than falsely flag it missing.
          console.warn(
            "[AudioEngine] _checkActiveLayersExist: getInfoAsync error for",
            uri,
            err,
          );
          return;
        }

        if (exists) return;

        // File is gone — stop the sound so it doesn't play silently.
        console.warn(
          "[AudioEngine] _checkActiveLayersExist: file missing, stopping layer",
          id,
          uri,
        );
        const missingLayer = this.layers[id];
        if (!missingLayer) return; // already removed by a concurrent path

        try {
          await missingLayer.sound.stopAsync();
          await missingLayer.sound.unloadAsync();
        } catch (_) {}

        delete this.layers[id];

        const missingError = new Error("FILE_MISSING");
        missingError.code = "FILE_MISSING";
        this._errorListeners.forEach((cb) => {
          try {
            cb({ id, error: missingError });
          } catch (_) {}
        });
      }),
    );

    // Notify after all checks are done so the UI refreshes once.
    this._notify();
  }

  async _currentVolume(sound) {
    const status = await sound.getStatusAsync();
    return status.isLoaded ? status.volume : 0;
  }

  // Cancel a fade and release an incoming swap sound that is no longer owned
  // by an active swap. Keeping this in one place is important because a newer
  // swap can invalidate an older one while its crossfade is still pending.
  async _discardSwapSound(sound) {
    if (!sound) return;
    if (sound.__audioEngineDiscarded) return;
    sound.__audioEngineDiscarded = true;

    if (this._fadeIntervals?.has(sound)) {
      const fade = this._fadeIntervals.get(sound);
      clearInterval(fade.handle);
      fade.resolve();
      this._fadeIntervals.delete(sound);
    }

    try {
      await sound.stopAsync();
      await sound.unloadAsync();
    } catch (_) {}
  }

  // Simple linear fade using a step interval. Good enough for ambient sound;
  // swap for an easing curve later if you want a more "natural" taper.
  //
  // _fadeIntervals stores { handle, resolve } per sound so that cancelling a
  // fade (from stopLayer or a competing _fadeTo call) can also resolve the
  // Promise, preventing any caller awaiting the old fade from leaking forever.
  _fadeTo(sound, from, to, durationMs) {
    return new Promise((resolve) => {
      const steps = Math.max(1, Math.round(durationMs / 100));
      const stepSize = (to - from) / steps;
      let current = from;
      let i = 0;

      this._fadeIntervals = this._fadeIntervals || new Map();

      // Cancel any previous fade on this sound and resolve its Promise so
      // callers awaiting it unblock immediately (no leaked pending Promises).
      if (this._fadeIntervals.has(sound)) {
        const prev = this._fadeIntervals.get(sound);
        clearInterval(prev.handle);
        prev.resolve();
      }

      const entry = { handle: null, resolve };

      const handle = setInterval(async () => {
        i += 1;
        current += stepSize;
        const clamped = Math.min(1, Math.max(0, current));
        try {
          await sound.setVolumeAsync(clamped);
        } catch (e) {
          // sound may have been unloaded mid-fade; just stop the interval
          clearInterval(handle);
          this._fadeIntervals.delete(sound);
          return resolve();
        }
        if (i >= steps) {
          clearInterval(handle);
          this._fadeIntervals.delete(sound);
          resolve();
        }
      }, 100);

      entry.handle = handle;
      this._fadeIntervals.set(sound, entry);
    });
  }

  // Seamlessly swap the audio file for every active layer that is currently
  // playing the given URI (e.g. after a track is re-imported from a new path).
  // A short crossfade hides the swap so the mix continues without a gap.
  async updateActiveLayersByUri(oldUri, newUri, { fadeMs = 1200 } = {}) {
    const matchingIds = Object.entries(this.layers)
      .filter(([, layer]) => layer.uri === oldUri)
      .map(([id]) => id);

    if (matchingIds.length === 0) return; // track not currently active

    await Promise.all(
      matchingIds.map(async (id) => {
        const layer = this.layers[id];
        if (!layer) return;

        // Track request order separately from active swap ownership. Starting
        // a newer load must not invalidate a viable crossfade until the newer
        // replacement has actually loaded and started successfully.
        const swapGeneration =
          Math.max(
            layer._swapRequestGeneration ?? 0,
            layer._swapGeneration ?? 0,
          ) + 1;
        layer._swapRequestGeneration = swapGeneration;

        // Load the replacement sound at zero volume so it starts silently.
        let newSound;
        try {
          ({ sound: newSound } = await Audio.Sound.createAsync(
            { uri: newUri },
            { isLooping: layer.loop ?? true, volume: 0 },
          ));
        } catch (err) {
          console.warn(
            "[AudioEngine] updateActiveLayersByUri: could not load new URI:",
            err,
          );
          return;
        }

        // If stopLayer was called while createAsync was in-flight, the layer
        // entry has already been cleaned up. Unload the new sound immediately
        // so it doesn't orphan.
        if (
          !this.layers[id] ||
          (layer._swapGeneration ?? 0) > swapGeneration
        ) {
          await this._discardSwapSound(newSound);
          return;
        }

        try {
          await newSound.playAsync();
        } catch (err) {
          await this._discardSwapSound(newSound);
          console.warn(
            "[AudioEngine] updateActiveLayersByUri: could not start new URI:",
            err,
          );
          return;
        }

        // The newer swap may have taken ownership while createAsync or
        // playAsync was in flight. Do not publish this sound as the current
        // incoming sound unless this swap still owns the layer.
        if (
          !this.layers[id] ||
          (layer._swapGeneration ?? 0) > swapGeneration
        ) {
          await this._discardSwapSound(newSound);
          return;
        }

        // Loading and playback both succeeded, so this request can now become
        // the active swap owner. A pending newer request does not reach this
        // point until it is also ready to take over.
        layer._swapGeneration = swapGeneration;

        const oldSound = layer.sound;
        const targetVol = layer.targetVolume;

        // A previous swap may already have started its crossfade. Invalidate
        // and release that incoming sound before this swap installs its own
        // marker, otherwise the older sound can become an orphan when the
        // newer swap commits.
        if (layer._swapSound && layer._swapSound !== newSound) {
          const previousSwapSound = layer._swapSound;
          layer._swapSound = null;
          await this._discardSwapSound(previousSwapSound);
        }

        if (
          !this.layers[id] ||
          layer._swapGeneration !== swapGeneration
        ) {
          await this._discardSwapSound(newSound);
          return;
        }

        // Expose the incoming sound on the layer so stopLayer can unload it
        // if a stop is requested while the crossfade is in progress.
        layer._swapSound = newSound;

        // Cancel any in-progress fade on the outgoing sound and resolve its
        // Promise so any awaiting caller unblocks rather than leaking.
        if (this._fadeIntervals?.has(oldSound)) {
          const prev = this._fadeIntervals.get(oldSound);
          clearInterval(prev.handle);
          prev.resolve();
          this._fadeIntervals.delete(oldSound);
        }

        // Crossfade: bring new sound up while bringing old sound down in parallel.
        const currentVol = await this._currentVolume(oldSound);
        await Promise.all([
          this._fadeTo(newSound, 0, targetVol, fadeMs),
          this._fadeTo(oldSound, currentVol, 0, fadeMs),
        ]);

        // If stopLayer intercepted the swap while the crossfade was in
        // progress it will have set layer._swapSound to null and already
        // unloaded newSound. Only commit the swap when we still own the
        // marker; otherwise bail out immediately — newSound is already
        // cleaned up and any further mutation of layer would race stopLayer.
        if (layer._swapGeneration !== swapGeneration) {
          // If the newer swap has not cleaned this sound up yet, take care of
          // it here. Clearing the marker first makes cleanup idempotent with
          // the newer swap's takeover path.
          if (layer._swapSound === newSound) {
            layer._swapSound = null;
            await this._discardSwapSound(newSound);
          }
          return;
        }

        if (layer._swapSound !== newSound) {
          return;
        }
        layer._swapSound = null;

        // If the layer was deleted between the crossfade resolving and the
        // ownership check above (e.g. a concurrent stopLayer whose fade
        // finished just after ours), unload newSound and exit cleanly.
        if (!this.layers[id]) {
          try {
            // The crossfade can finish before the stopLayer deletion reaches
            // this continuation, so newSound may already be at target volume.
            // Fade it back to silence to avoid an audible replacement during
            // the sleep-timer stop race.
            const currentNewVolume = await this._currentVolume(newSound);
            await this._fadeTo(newSound, currentNewVolume, 0, fadeMs);
          } catch (_) {}
          // Cleanup must still run if reading or fading the volume fails.
          await this._discardSwapSound(newSound);
          return;
        }

        // Unload the outgoing sound.
        try {
          await oldSound.stopAsync();
          await oldSound.unloadAsync();
        } catch (_) {}

        // Swap the layer's internals in-place so volume/stop/pause still work.
        layer.sound = newSound;
        layer.uri = newUri;
        layer.source = { uri: newUri };
        this._attachLayerStatus(id, layer, newSound);
      }),
    );

    this._notify();
  }

  // ---- Sleep timer -------------------------------------------------------
  // duration: total minutes until silence
  // fadeWindowMinutes: how long before the end to start fading everything out
  startSleepTimer(durationMinutes, fadeWindowMinutes = 10, onTick, onComplete) {
    this.cancelSleepTimer();

    const totalMs = durationMinutes * 60 * 1000;
    const fadeStartMs = totalMs - fadeWindowMinutes * 60 * 1000;
    const startedAt = Date.now();

    this.sleepTimerHandle = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remainingMs = totalMs - elapsed;
      onTick?.(Math.max(0, Math.round(remainingMs / 1000)));

      if (elapsed >= fadeStartMs && !this._fadingOutForSleep) {
        this._fadingOutForSleep = true;
        // Cancel any pending route-change / BT-reconnect resume timers immediately.
        // If a call interrupted mid-fade the timers are already running;
        // leaving them alive would log a spurious no-op or, on devices where focus
        // events arrive out-of-order, leave _interrupted in a stale state.
        if (this._routeResumeTimer) {
          clearTimeout(this._routeResumeTimer);
          this._routeResumeTimer = null;
        }
        if (this._btQuickResumeTimer) {
          clearTimeout(this._btQuickResumeTimer);
          this._btQuickResumeTimer = null;
        }
        this.stopAll({ fadeMs: fadeWindowMinutes * 60 * 1000 });
      }

      if (elapsed >= totalMs) {
        this.cancelSleepTimer();
        this._fadingOutForSleep = false;
        // If a BT device disconnected during the fade, _interrupted may still
        // be true. The AppState handler guards against resuming while
        // _fadingOutForSleep is set (Task #96), but once we clear that flag here
        // a subsequent foreground event with _interrupted = true would call
        // _resumeAllLayers() and restart audio the user expected the sleep timer
        // to silence permanently. Clearing _interrupted at fade-end closes that
        // window. Cancel any pending BT-reconnect timers for the same reason —
        // they carry stale intent from before the timer completed.
        this._interrupted = false;
        if (this._routeResumeTimer) {
          clearTimeout(this._routeResumeTimer);
          this._routeResumeTimer = null;
        }
        if (this._btQuickResumeTimer) {
          clearTimeout(this._btQuickResumeTimer);
          this._btQuickResumeTimer = null;
        }
        // Clear the paused flag so a fresh session can start immediately
        // after the timer completes, even if the user tapped lock-screen
        // pause mid-fade (which would have set _paused = true).
        this._paused = false;
        onComplete?.();
      }
    }, 1000);
  }

  cancelSleepTimer() {
    if (this.sleepTimerHandle) {
      clearInterval(this.sleepTimerHandle);
      this.sleepTimerHandle = null;
    }
  }
}

export const AudioEngine = new AudioEngineClass();
